
import Vue from 'vue';
import { Data as ArticleData, Headlines as ArticleHeadlines } from '@root/modules/article/types/article';
import { DataWithHref, Data as HeadlinesData } from '@root/modules/headlines/types/headlines';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import ArticleHeadline from './ArticleHeadline.vue';
import Swiper from '@core/components/UI/swiper/Swiper.vue';
import { HeadlinePublishDate, HeadlineTitle } from '@headlines/components/fragments';

interface Methods {
  addClickData: (headline: HeadlinesData, params: Record<string, any>) => Record<string, any>;
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadline: (headline: HeadlinesData) => DataWithHref;
  swiperOptions: (container: string) => Record<string, any>;
  initObserver: () => void;
  destroyObserver: () => void;
}

interface Computed {
  headlineList: DataWithHref[];
  isCrArticle: boolean;
  crIndex: number | null;
  cxenseArticles: {
    id: string;
    clickUrl: string;
  }[];
  cxenseHeadlines: HeadlinesData[];
  isBlockRendered: boolean;
}

interface Props {
  article: ArticleData;
  headlines: ArticleHeadlines;
  limit: number;
  start: number;
  recommendedLimit: number;
  recommendedStart: number;
  betweenArticles: boolean;
  dataSource: string;
}

interface Data {
  domains: string | string[];
  locale: string;
  cxenseHeadlinesLimit: number;
  observer: IntersectionObserver | null;
  betweenArticlesVariant: number;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleHeadline,
    HeadlinePublishDate,
    HeadlineTitle,
    Swiper,
  },
  props: {
    article: {
      type: Object,
      required: false,
      default: null,
    },
    headlines: {
      type: Array,
      required: false,
      default: null,
    },
    limit: {
      type: Number,
      required: false,
      default: 6,
    },
    start: {
      type: Number,
      required: false,
      default: 0,
    },
    recommendedLimit: {
      type: Number,
      required: false,
      default: 5,
    },
    recommendedStart: {
      type: Number,
      required: false,
      default: 0,
    },
    betweenArticles: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataSource: {
      type: String,
      required: false,
      default: 'cxense',
    },
  },
  data() {
    const { domain, locale } = this.$channelConfig('settings');
    return {
      domains: domain,
      locale,
      cxenseHeadlinesLimit: 20,
      observer: null,
      betweenArticlesVariant: Math.floor(Math.random() * 2),
    };
  },
  computed: {
    headlineList() {
      const headlines = (this.headlines && this.headlines.slice(this.start, this.limit).map((headline) => this.getHeadline(headline))) || [];

      return headlines;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    crIndex() {
      if (this.isCrArticle) {
        const id = this.article.id;
        const crArticles = this.$store.state.article.crArticles;
        return crArticles.indexOf(id) + 1;
      }

      return null;
    },
    cxenseArticles() {
      return this.$store.getters['article/getArticleCxenseArticles'] || [];
    },
    cxenseHeadlines() {
      let cxenseHeadlines = this.$store.getters['article/getArticleCxenseHeadlines'] || [];
      if (cxenseHeadlines.length) {
        const start = this.isCrArticle && this.crIndex ? (this.crIndex % 5) * this.recommendedLimit : this.recommendedStart;
        const end = start + this.recommendedLimit;

        cxenseHeadlines = cxenseHeadlines.slice(start, end);
      }

      return cxenseHeadlines;
    },
    isBlockRendered() {
      return !!((this.betweenArticles && this.cxenseHeadlines.length) || this.headlineList.length);
    },
  },
  watch: {
    isBlockRendered(value) {
      if (value) {
        this.$nextTick(() => {
          this.initObserver();
        });
      }
    },
  },
  methods: {
    addClickData(headline: HeadlinesData, params: Record<string, any>) {
      const clickData: Record<string, any> = {};

      clickData.type = params.type;

      // Add test variant parameter if defined
      if (params.variant) {
        clickData.variant = params.variant;
      }

      return { ...headline, clickData };
    },
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domains, fakeDomainEnabled });
    },
    getCxenseHeadline(headline) {
      const cxenseArticle = this.cxenseArticles.find((article) => article.id === String(headline.id));
      return cxenseArticle ? { ...headline, href: cxenseArticle?.clickUrl } : this.getHeadline(headline);
    },
    swiperOptions(container: string) {
      const swiperOptions = {
        spaceBetween: 12,
        initialSlide: 0,
        loop: false,
        autoplay: false,
        slidesPerView: 1.2,
        slidesPerGroup: 1,
        pagination: {
          enabled: false,
        },
        navigation: {
          nextEl: `.${container} .swiper-button-next`,
          prevEl: `.${container} .swiper-button-prev`,
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        breakpoints: {
          768: {
            freeMode: false,
            slidesPerView: 3,
            slidesPerGroup: 3,
            loop: false,
            navigation: {
              enabled: true,
            },
          },
        },
      };

      return swiperOptions;
    },
    initObserver() {
      const blockElement = this.$refs[`related-articles-${this.article.id}`] as Element;

      if (!blockElement) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            // Send block impression event
            const eventParams: any = {};

            eventParams.type = this.betweenArticles ? 'related-articles-between-articles' : 'related-articles-legacy';

            if (this.betweenArticles) {
              eventParams.variant = this.betweenArticlesVariant === 1 ? 'b' : 'a';
            }

            this.$event?.send('articles-block-impression', eventParams);

            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
          }
        },
        { root: null }
      );

      this.observer.observe(blockElement);
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
  },
  mounted() {
    if (this.isBlockRendered) {
      this.initObserver();
    }
  },
  beforeDestroy() {
    this.destroyObserver();
  },
});
